import {APIService} from "../../common/APIService";
import {Project, ProjectStatus} from "../../dto/Project";
import {ProjectDisk, ProjectFileType} from "../../dto/ProjectDisk";
import {ProjectRelationStatus, RelationType} from "../../dto/ProjectRelation";
import {NoteType, ProjectNote} from "../../dto/ProjectNote";
import {ProjectSettings} from "../../dto/ProjectSettings";
import {Task} from "../../dto/Task";

export class ProjectService extends APIService {
  public async browse() {
    const json = await this.getJson<{data: object[]}>(`projects`)
    return json.data.map(item => Project.fromJson(item))
  }

  public async read(projectId: string): Promise<Project> {
    const json = await this.getJson<{data: object}>(`projects/${projectId}`)
    return Project.fromJson(json.data)
  }

  public async add(description: string|null, type: string, primaryContactId: string|null, memo: string|null, streetname: string|null, houseNumber: string|null, houseNumberAddition: string|null, city: string|null, zipcode: string|null, name: string|null, phone: string|null, email: string|null, floorColor?: string|null) {
    const json = await this.postJson<{data: object}>('projects', {
      'description': description,
      'primary_contact_id': primaryContactId,
      'type': type,
      ...(memo != null ? {'memo': memo} : {}),
      ...(streetname != null ? {'delivery_address_streetname': streetname} : {}),
      ...(houseNumber != null ? {'delivery_address_house_number': houseNumber} : {}),
      ...(houseNumberAddition != null ? {'delivery_address_house_number_addition': houseNumberAddition} : {}),
      ...(city != null ? {'delivery_city': city} : {}),
      ...(zipcode != null ? {'delivery_zipcode': zipcode} : {}),
      ...(name != null ? {'delivery_name': name} : {}),
      ...(phone != null ? {'delivery_phone': phone} : {}),
      ...(email != null ? {'delivery_email': email} : {}),
      ...(floorColor !== undefined ? {'floor_color': floorColor,} : {}),
    })
    return Project.fromJson(json.data)
  }

  public async editRelation(projectId: string, relationId: string, relationType: RelationType, relationStatus: ProjectRelationStatus, projectSettings?: ProjectSettings): Promise<void> {
    await this.putJson<{data: object}>(`projects/${projectId}/relations/${relationId}`, {
      'relation_type': relationType,
      'relation_status': relationStatus,
      ...(projectSettings !== undefined ? {
        'should_request_purchase_order': projectSettings.shouldRequestPurchaseOrder,
        'should_send_man_day_sheet': projectSettings.shouldSendManDaySheet,
        'should_use_g_account': projectSettings.shouldUseGAccount,
        'requires_project_number': projectSettings.requiresProjectNumber,
      } : {}),
    })
  }

  public async deleteRelation(projectId: string, relationId: string): Promise<void> {
    await this.deleteJson<{data: object}>(`projects/${projectId}/relations/${relationId}`)
  }

  public async editContact(projectId: string, relationId: string, contactId: string): Promise<void> {
    await this.postJson<{data: object}>(`projects/${projectId}/relations/${relationId}/contacts`, {
      'contact_id': contactId,
    })
  }
  public async deleteContact(projectId: string, relationId: string, contactId: string): Promise<void> {
    await this.deleteJson<{data: object}>(`projects/${projectId}/relations/${relationId}/contacts/${contactId}`)
  }

  public async editDetails(projectId: string, description: string|null, type: string, primaryContactId: string|null, status: ProjectStatus, memo: string|null, streetname: string|null, houseNumber: string|null, houseNumberAddition: string|null, city: string|null, zipcode: string|null, name: string|null, phone: string|null, email: string|null, selectedAssignee?: string|null, floorColor?: string|null) {
    await this.putJson<{data: object}>(`projects/${projectId}`, {
      'description': description,
      'type': type,
      'status': status,
      'primary_contact_id': primaryContactId,
      'memo': memo,
      'delivery_address_streetname': streetname,
      'delivery_address_house_number': houseNumber,
      'delivery_address_house_number_addition': houseNumberAddition,
      'delivery_city': city,
      'delivery_zipcode': zipcode,
      'delivery_name': name,
      'delivery_phone': phone,
      'delivery_email': email,
      ...(selectedAssignee !== undefined ? {
        'account_manager_id': selectedAssignee,
      } : {}),
      ...(floorColor !== undefined ? {
        'floor_color': floorColor,
      } : {}),
    })
  }

  async editPrimaryContact(id: string, selectedContact: string) {
    await this.putJson<{data: object}>(`projects/${id}/primary-contact`, {
      'primary_contact_id': selectedContact,
    })
  }

  public async readDisk(projectId: string) {
    const json = await this.getJson<{data: object}>(`projects/${projectId}/disk`)
    return ProjectDisk.fromJson(json.data)
  }
  public async addDiskFile(projectId: string, type: ProjectFileType, file: File, fileName?: string) {
    const formData = new FormData();
    formData.append('file', file, fileName ? fileName : file.name);
    formData.append('type', type);
    await this.postForm(`projects/${projectId}/disk/upload`, formData)
  }
  public async addDiskFileByPath(projectId: string, path: string, file: File, fileName?: string) {
    const formData = new FormData();
    formData.append('file', file, fileName ? fileName : file.name);
    formData.append('path', path);
    await this.postForm(`projects/${projectId}/disk/upload`, formData)
  }
  public async moveDiskFile(projectId: string, fileName: string, oldFileType: ProjectFileType, newFileType: ProjectFileType) {
    await this.postJson(`projects/${projectId}/disk/move_file`, {
      file_name: fileName,
      old_type: oldFileType,
      new_type: newFileType,
    })
  }
  public async moveDiskFileByPath(projectId: string, oldPath: string, newPath: string) {
    await this.postJson(`projects/${projectId}/disk/move_file`, {
      old_path: oldPath,
      new_path: newPath,
    })
  }
  public async addDiskFileFromTemplate(projectId: string, fileTemplateId: string, type: ProjectFileType, name: string) {
    const json = await this.postJson<{data: {url: string}}>(`projects/${projectId}/disk/add_from_template`, {
      template_id: fileTemplateId,
      type: type,
      filename: name
    })
    return json.data.url
  }

  public async deleteDiskFile(projectId: string, fileName: string, fileType: ProjectFileType) {
    await this.postJson(`projects/${projectId}/disk/delete_file`, {
      file_name: fileName,
      type: fileType,
    })
  }

  public async deleteDiskFileByPath(projectId: string, path: string) {
    await this.postJson(`projects/${projectId}/disk/delete_file`, {
      path: path,
    })
  }


  public async getDistance(projectId: string) {
    const json = await this.getJson<{data: {distance: number|null}}>(`projects/${projectId}/route-distance`)
    return json.data.distance
  }

  public async addProjectNote(projectId: string, type: NoteType, note: string) {
    const json = await this.postJson<{data: object}>(`projects/${projectId}/notes`, {
      'type': type,
      'note': note,
    })
    return ProjectNote.fromJson(json.data)
  }

  public async editProjectNote(projectNoteId: string, projectId: string, type: NoteType, note: string) {
    const json = await this.putJson<{data: object}>(`projects/${projectId}/notes/${projectNoteId}`, {
      'type': type,
      'note': note,
    })
    return ProjectNote.fromJson(json.data)
  }

  public async deleteProjectNote(projectId: string, projectNoteId: string) {
    await this.deleteJson<{data: object}>(`projects/${projectId}/notes/${projectNoteId}`)
  }

  public async fetchProjectHours(projectId: string, ranges: { from: Date, to: Date }[]) {
    const response = await this.postRaw<object[]>(`projects/${projectId}/hours`, {
      'ranges': ranges.map((range) => ({
        'from_date': range.from,
        'to_date': range.to,
      })),
    });
    return response.blob()
  }

  public async fetchEmailsForProjects(taskIds: string[]) {
    return await this.postJson<string[]>(`projects/emails/fetch`, {
      'task_ids': taskIds
    });
  }

  public async sendEmailsForProjects(subject: string, content: string, emails: string[], files?: File[]) {
    const formData = new FormData()

    formData.append('subject', subject)
    formData.append('content', content)

    emails.forEach(email => {
      formData.append('emails[]', email)
    })

    files?.forEach((file) => {
      formData.append('files[]', file, file.name)
    })

    await this.putForm<{ data: object[] }>(`projects/emails/send`, formData)
  }

  public async getCoordinates(projectId: string): Promise<{lat: number, lng: number}> {
    const json = await this.getJson<{data: {lat: number, lng: number}}>(`projects/${projectId}/coordinates`)
    return json.data
  }
}